import { Component, inject, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Avatar } from 'primeng/avatar';
import { Tooltip } from 'primeng/tooltip';
import { Ripple } from 'primeng/ripple';
import { Popover } from 'primeng/popover';
import { Select } from 'primeng/select';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { AuthService, AccountItem } from '../../core/auth/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { LayoutService } from '../layout.service';
import { environment } from '../../../environments/environment';

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard':     'Dashboard',
  '/users':         'User Management',
  '/providers':     'Providers',
  '/facesheet':     'Face Sheet',
  '/writeoff':      'Write-Off Queue',
  '/authorization': 'Authorization',
};

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [FormsModule, Menu, Avatar, Tooltip, Ripple, Popover, Select, ConfirmDialog],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.css']
})
export class TopbarComponent implements OnInit, OnDestroy {
  layoutService  = inject(LayoutService);
  theme          = inject(ThemeService);
  private auth         = inject(AuthService);
  private router       = inject(Router);
  private platform     = inject(PLATFORM_ID);
  private confirmation = inject(ConfirmationService);

  pageTitle    = 'Dashboard';
  isDark       = false;
  appVersion   = environment.version;
  releaseDate  = environment.releaseDate;
  signingOut   = signal(false);
  accounts     = signal<AccountItem[]>([]);
  selectedAccountId = signal<number>(0);
  private sub!: Subscription;

  presets = [
    { key: 'aura',     label: 'Aura'     },
    { key: 'lara',     label: 'Lara'     },
    { key: 'nora',     label: 'Nora'     },
    { key: 'material', label: 'Material' },
  ];

  userMenuItems: MenuItem[] = [
    {
      items: [
        {
          label: 'Sign Out',
          icon: 'pi pi-sign-out',
          command: () => this.signOut()
        }
      ]
    }
  ];

  get user()         { return this.auth.getCurrentUser(); }
  get userName()     { const u = this.user; return u ? `${u.firstName} ${u.lastName}` : 'User'; }
  get userRole()     { return this.user?.role ?? 'user'; }
  get userInitials() {
    const u = this.user;
    if (!u) return 'U';
    return `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase();
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platform)) {
      this.isDark = this.theme.isDarkMode();
      this.selectedAccountId.set(this.auth.getAccountId());
    }
    this.sub = new Subscription();

    // Keep accounts dropdown in sync
    this.sub.add(
      this.auth.accounts$.subscribe(list => this.accounts.set(list))
    );

    // Load accounts — works for fresh login (cached) and page refresh (re-fetches)
    const user = this.auth.getCurrentUser();
    if (user?.id) {
      this.sub.add(
        this.auth.getUserAccounts(user.id).subscribe({
          next: res => {
            if (res.success && res.data?.length) {
              // If no account is selected yet, default to first
              if (!this.auth.getAccountId() && res.data[0]) {
                this.auth.setAccountId(res.data[0].accountId);
                this.selectedAccountId.set(res.data[0].accountId);
              }
            }
          }
        })
      );
    }

    this.updateTitle(this.router.url);
    this.sub.add(
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe((e: any) => this.updateTitle(e.urlAfterRedirects ?? e.url))
    );
  }

  onAccountChange(accountId: number): void {
    if (accountId === this.selectedAccountId()) return;

    const fromAccount = this.accounts().find(a => a.accountId === this.selectedAccountId());
    const toAccount   = this.accounts().find(a => a.accountId === accountId);
    const fromName = fromAccount?.accountAbbreviation ?? '';
    const toName   = toAccount?.accountAbbreviation   ?? '';

    this.confirmation.confirm({
      key: 'account-switch',
      header: 'Account Switch Confirmation',
      message: `<p style="margin:0 0 0.75rem">Switching from <strong>${fromName}</strong> to <strong>${toName}</strong>.</p><ul style="margin:0;padding-left:1.25rem;line-height:2"><li>All active sessions with <strong>${fromName}</strong> will be closed.</li><li>This activity will be logged.</li></ul>`,
      acceptLabel: 'Switch',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.selectedAccountId.set(accountId);
        this.auth.setAccountId(accountId);
        // Navigate to blank route then back to force full component destruction & data reload
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/_blank', { skipLocationChange: true }).then(() => {
          this.router.navigateByUrl(currentUrl);
        });
      },
      reject: () => {
        // revert the select back to current account
        const current = this.selectedAccountId();
        this.selectedAccountId.set(-1);
        setTimeout(() => this.selectedAccountId.set(current));
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private updateTitle(url: string): void {
    const key = Object.keys(ROUTE_TITLES).find(k => url.includes(k));
    this.pageTitle = key ? ROUTE_TITLES[key] : 'Client Portal';
  }

  toggleMenu(): void {
    this.layoutService.toggleMenu();
  }

  toggleDark(): void {
    this.isDark = !this.isDark;
    this.theme.toggleDarkMode(this.isDark);
  }

  selectPalette(key: string): void {
    this.theme.setColorPalette(key);
  }

  selectPreset(key: string): void {
    this.theme.setTheme(key);
  }

  signOut(): void {
    if (this.signingOut()) return;
    this.signingOut.set(true);
    this.auth.signOut();
  }
}
